import { message } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Card, Form, FormGroup, Modal, ModalBody, Spinner } from "reactstrap";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  PaginationComponent,
  RSelect,
} from "../../../components/Component";
import DeleteModal from "../../../components/DeleteModal/DeleteModal";
import { ReferralRewardsTable } from "../../../components/table/SpecialTable";
import { authHeader } from "../../../header";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";

const referralStatusOptions = [
  { label: "Plan Created", value: "PLAN_CREATED" },
  { label: "Registered", value: "REGISTERED" },
  { label: "Wallet Funded", value: "WALLET_FUNDED" },
];

const rewardTypeOptions = [
  { label: "Flat", value: "FLAT" },
  { label: "Percentage", value: "PERCENTAGE" },
];

const initialFormData = {
  referralStatus: "",
  rewardType: "",
  targetPlanId: "",
  value: "",
};

export const ReferralRewardsManagement = () => {
  const [modal, setModal] = useState({ add: false, delete: false });
  const [formData, setFormData] = useState(initialFormData);
  const [rewards, setRewards] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadBtn, setIsLoadBtn] = useState(false);
  const [deleteId, setDeleteId] = useState();
  const [editId, setEditId] = useState(null);

  const { handleSubmit } = useForm();

  const resetForm = () => setFormData(initialFormData);

  const getReferralRewards = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v3/referral-rewards?pageNo=${currentPage}&pageSize=${itemPerPage}`,
        {
          headers: authHeader(),
        }
      );
      const { data } = response;
      if (data) {
        setRewards(data.content ?? []);
        setTotalElements(data.totalElements ?? 0);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getAllProducts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/auth/trproduct`, {
        headers: authHeader(),
      });
      const { data } = await response.data;
      const resultData = data.body;
      if (resultData) {
        setProducts(resultData.filter(({ status }) => status === "ACTIVE"));
      }
    } catch (error) {}
  };

  const buildPayload = () => ({
    referralStatus: formData.referralStatus,
    rewardType: formData.rewardType,
    targetPlanId: formData.targetPlanId,
    value: formData.value,
  });

  const createReferralReward = async () => {
    try {
      setIsLoadBtn(true);
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/v3/referral-rewards`, buildPayload(), {
        headers: authHeader(),
      });
      const data = await response.data;
      if (data) {
        getReferralRewards();
        setIsLoadBtn(false);
        setModal({ add: false });
        resetForm();
        message.success({ content: data.message || "Referral reward created successfully" });
      }
    } catch (error) {
      message.error({ content: error?.response?.data?.message || "Unable to create referral reward" });
      setIsLoadBtn(false);
    }
  };

  const updateReferralReward = async () => {
    try {
      setIsLoadBtn(true);
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/v3/referral-rewards/${editId}`,
        buildPayload(),
        {
          headers: authHeader(),
        }
      );
      const data = await response.data;
      if (data) {
        getReferralRewards();
        setIsLoadBtn(false);
        setModal({ add: false });
        setEditId(null);
        resetForm();
        message.success({ content: data.message || "Referral reward updated successfully" });
      }
    } catch (error) {
      message.error({ content: error?.response?.data?.message || "Unable to update referral reward" });
      setIsLoadBtn(false);
    }
  };

  const deleteReferralReward = async () => {
    try {
      setIsLoadBtn(true);
      const response = await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/v3/referral-rewards/${deleteId}`, {
        headers: authHeader(),
      });
      const data = await response.data;
      if (data) {
        getReferralRewards();
        setIsLoadBtn(false);
        setModal({ delete: false });
        message.success({ content: data.message || "Referral reward deleted successfully" });
      }
    } catch (error) {
      message.error({ content: error?.response?.data?.message || "Unable to delete referral reward" });
      setIsLoadBtn(false);
      setModal({ delete: false });
    }
  };

  const onFormSubmit = () => {
    if (!(formData.referralStatus && formData.rewardType && formData.targetPlanId && formData.value !== "")) {
      message.error({ content: "Please fill all fields" });
      return;
    }
    if (formData.rewardType === "PERCENTAGE" && Number(formData.value) > 100) {
      message.error({ content: "Percentage value cannot be more than 100" });
      return;
    }
    if (editId) {
      updateReferralReward();
    } else {
      createReferralReward();
    }
  };

  const onCreateClick = () => {
    setEditId(null);
    resetForm();
    setModal({ add: true });
  };

  const onEditClick = (item) => {
    setEditId(item.id);
    setFormData({
      referralStatus: item.referralStatus ?? "",
      rewardType: item.rewardType ?? "",
      targetPlanId: item.targetPlanId ?? "",
      value: item.value ?? "",
    });
    setModal({ add: true });
  };

  const onDeleteClick = (item) => {
    setDeleteId(item.id);
    setModal({ delete: true });
  };

  const onFormCancel = () => {
    setModal({ add: false, delete: false });
    setEditId(null);
    resetForm();
  };

  useEffect(() => {
    getReferralRewards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    getAllProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const productOptions =
    products?.map((prod) => ({
      label: prod.productName,
      value: prod.id,
    })) ?? [];

  return (
    <React.Fragment>
      <Head title="Referral Rewards"></Head>
      <Content>
        <Block size="lg">
          <BlockHead>
            <BlockHeadContent>
              <div className="d-flex justify-content-between align-items-center">
                <BlockTitle tag="h4">Referral Rewards</BlockTitle>
                <div className="d-flex align-items-center justify-center">
                  <Button color="primary" onClick={onCreateClick}>
                    <Icon name="plus"></Icon>
                    <span>Create Referral Reward</span>
                  </Button>
                </div>
              </div>
            </BlockHeadContent>
          </BlockHead>
          <Card className="card-bordered card-preview" style={{ width: "100%", overflowX: "auto" }}>
            {isLoading ? (
              <Spinner
                color="primary"
                style={{
                  height: "3rem",
                  width: "3rem",
                  margin: "0 auto",
                }}
              />
            ) : (
              <ReferralRewardsTable
                data={rewards}
                products={products}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
              />
            )}
          </Card>
          <div className="card-inner">
            {totalElements > 0 ? (
              <PaginationComponent
                itemPerPage={itemPerPage}
                totalItems={totalElements}
                paginate={paginate}
                currentPage={currentPage}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">No referral reward found</span>
              </div>
            )}
          </div>
        </Block>

        <Modal isOpen={modal.add} toggle={() => setModal({ add: false })} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">{editId ? "Edit Referral Reward" : "Create Referral Reward"}</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(onFormSubmit)}>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Referral Status</label>
                      <RSelect
                        placeholder="Select Referral Status"
                        options={referralStatusOptions}
                        value={referralStatusOptions.filter(({ value }) => value === formData.referralStatus)[0] ?? null}
                        onChange={(e) => setFormData({ ...formData, referralStatus: e.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Reward Type</label>
                      <RSelect
                        placeholder="Select Reward Type"
                        options={rewardTypeOptions}
                        value={rewardTypeOptions.filter(({ value }) => value === formData.rewardType)[0] ?? null}
                        onChange={(e) => setFormData({ ...formData, rewardType: e.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Target Plan</label>
                      <RSelect
                        placeholder="Select Target Plan"
                        options={productOptions}
                        value={productOptions.filter(({ value }) => value === formData.targetPlanId)[0] ?? null}
                        onChange={(e) => setFormData({ ...formData, targetPlanId: e.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">
                        {formData.rewardType === "PERCENTAGE"
                          ? "Reward Value (%)"
                          : formData.rewardType === "FLAT"
                          ? "Reward Value (₦)"
                          : "Reward Value"}
                      </label>
                      <input
                        type="number"
                        name="value"
                        placeholder={
                          formData.rewardType === "PERCENTAGE"
                            ? "e.g. 10 for 10% of the plan"
                            : formData.rewardType === "FLAT"
                            ? "e.g. 5000 for a flat ₦5,000 reward"
                            : "Select a reward type first"
                        }
                        min="0"
                        max={formData.rewardType === "PERCENTAGE" ? "100" : undefined}
                        value={formData.value}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            value: e.target.value === "" ? "" : parseFloat(e.target.value),
                          })
                        }
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        <Button color="primary" size="md" type="submit">
                          {isLoadBtn && <Spinner size="sm" />}
                          {isLoadBtn ? " Loading" : editId ? "Update" : "Submit"}
                        </Button>
                      </li>
                      <li>
                        <Button
                          onClick={(ev) => {
                            ev.preventDefault();
                            onFormCancel();
                          }}
                          className="link link-light"
                        >
                          Cancel
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <DeleteModal
          isOpen={modal.delete}
          onClose={() => setModal((prev) => ({ ...prev, delete: false }))}
          title="this referral reward"
          deleteFunc={deleteReferralReward}
          onFormCancel={onFormCancel}
        />
      </Content>
    </React.Fragment>
  );
};
